import { SEASON_KINDS, SEASON_SPAN_WORDS, SEASON_SPANS, seasonSlotMonths, type ArtDto, type ArtRowDto, type ProjectArtDto, type SeasonArtRowDto, type SeasonKind } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText, queryClient, type ApiError } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Button, ErrorLine, LoadError, Loading, Page, Section, Tag, TextLink, Title } from '../../components/ui.tsx';
import { isAdmin, useMe } from '../../session.tsx';
import { ProjectCrumb } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { dateWords } from '../../days.ts';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const SEASON_WAYS: Record<SeasonKind, { title: string; note: string }> = {
  seasons_across: { title: 'Across', note: 'The land, left to right: under the funnel on wide screens.' },
  seasons_down: { title: 'Down', note: 'The plants, top to bottom: beside the funnel on phones.' },
};

/**
 * A project's banners, one per month (style guide v2 §13C, §14): an internal
 * gallery of what was painted, cropped as it lies full-bleed behind a project
 * home on a wide screen and on a phone (the same for NSB and the client), and
 * what the research into the place found; then the painted years behind the
 * price funnel. Regenerating lives here, for admins.
 */
export function Banners() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const { data, error } = useQuery<ProjectArtDto, ApiError>({
    queryKey: ['project', projectId, 'art'],
    queryFn: () => api<ProjectArtDto>(`/projects/${projectId}/art`),
    // While research or painting is under way, look again every few seconds.
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.researching || [...d.months, ...d.seasons].some((m) => m.status === 'pending') || d.icon?.status === 'pending') ? 4000 : false;
    },
  });
  const [now] = useState(() => new Date().getMonth() + 1);
  const [fresh, setFresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useFeedbackRecord(projectId, project ? { type: 'banners', id: projectId, label: `${project.name} — banners` } : null);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const run = async (path: string, body: unknown = {}) => {
    setBusy(true);
    setErr(null);
    try {
      await api(path, { body });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const byMonth = new Map(data.months.map((m) => [m.month, m]));
  const ready = data.months.filter((m) => m.status === 'ready').length;
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title>Banners</Title>
      <p className="max-w-2xl text-ink-2">
        A watercolor of the place for each month, painted at setup from research into where the project is. The project shows the one for the
        month it is, to you and to the client. They are atmosphere, never a picture of the work.
      </p>
      {data.place ? <p className="meta mt-2">Painted from {data.place}</p> : null}
      {data.usingDefault ? (
        <p className="mt-2 text-ink-2">
          Until its own are painted, it shows NSB’s, painted from where NSB is. <TextLink to="/settings/art">Settings, Art</TextLink>
        </p>
      ) : null}

      {data.blocked ? (
        <p className="mt-8 text-ink-2">
          {data.blocked}{' '}
          {data.blocked.includes('in Settings') ? <TextLink to="/settings/connections">Open Settings</TextLink> : <TextLink to={`/p/${projectId}/settings`}>Set the site</TextLink>}
        </p>
      ) : admin ? (
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
          <Button tone="secondary" busy={busy} onClick={() => void run(`/projects/${projectId}/art`, { freshResearch: fresh })}>
            {data.months.length ? 'Paint it all again' : 'Paint the twelve months and the seasons'}
          </Button>
          <label className="flex min-h-11 items-center gap-2.5 text-sm text-ink-2">
            <input type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)} />
            Research the place again first
          </label>
        </div>
      ) : null}
      {data.researching ? <p className="label mt-6 animate-pulse">Researching the place</p> : null}
      <ErrorLine error={err} />

      <Section title="Icon">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="h-24 w-36 shrink-0 overflow-hidden rounded-[2px] bg-inset">
            {data.icon?.art ? <img src={`${data.icon.art.url}?w=320`} alt={data.icon.art.alt} className="h-full w-full object-cover" /> : null}
          </div>
          <div>
            <p className="max-w-md text-ink-2">The project’s small watercolor on the Projects page, painted from its name and what it is: the setting, never the building.</p>
            <p className="meta mt-1">{data.icon ? statusWord(data.icon) : 'Not painted'}</p>
            {data.icon?.error ? <p className="text-sm text-alert">{data.icon.error}</p> : null}
            {admin ? (
              <Button tone="quiet" disabled={busy || data.icon?.status === 'pending'} onClick={() => void run(`/projects/${projectId}/art/icon`)}>
                {data.icon ? 'Paint again' : 'Paint it'}
              </Button>
            ) : null}
          </div>
        </div>
      </Section>

      {data.research ? (
        <Section title="What research found">
          <p className="max-w-2xl text-ink-2">{data.research.summary}</p>
          {data.research.plants.length ? (
            <p className="mt-3 max-w-2xl text-[0.875rem] text-ink-2">
              <span className="label mr-2">Plants</span>
              {data.research.plants.join(' · ')}
            </p>
          ) : null}
          {data.research.sources.length ? (
            <ul className="meta mt-3 space-y-1">
              {data.research.sources.map((s) => (
                <li key={s.url}>
                  <a className="link" href={s.url} target="_blank" rel="noreferrer">
                    {s.title || hostname(s.url)}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="meta mt-3">Researched {dateWords(data.research.researchedAt, 'date')}</p>
        </Section>
      ) : null}

      <Section title="Months" aside={<span className="label">{ready} of 12 ready</span>}>
        <div className="label hidden grid-cols-[9rem_4fr_1fr_7rem] gap-4 pb-2 sm:grid">
          <span />
          <span>Wide screen</span>
          <span>Phone</span>
          <span />
        </div>
        <ul className="divide-y divide-rule border-y border-rule">
          {MONTHS.map((name, i) => {
            const m = byMonth.get(i + 1);
            return (
              <li key={name} className="grid gap-x-4 gap-y-3 py-4 sm:grid-cols-[9rem_4fr_1fr_7rem] sm:items-center">
                <div>
                  <span className="font-display text-[1.125rem]">{name}</span>
                  {i + 1 === now ? (
                    <span className="ml-2">
                      <Tag tone="cedar">now</Tag>
                    </span>
                  ) : null}
                  <span className="meta block">{statusWord(m)}</span>
                  {m?.error ? <span className="block text-sm text-alert">{m.error}</span> : null}
                </div>
                {m?.art ? (
                  <>
                    <Crop art={m.art} aspect="aspect-[12/5]" w={1024} />
                    <Crop art={m.art} aspect="aspect-square" />
                  </>
                ) : (
                  <div className="aspect-[12/5] rounded-[2px] bg-inset sm:col-span-2" />
                )}
                {admin && !data.blocked ? (
                  <Button tone="quiet" disabled={busy || m?.status === 'pending'} onClick={() => void run(`/projects/${projectId}/art/months/${i + 1}`)}>
                    Paint again
                  </Button>
                ) : (
                  <span />
                )}
              </li>
            );
          })}
        </ul>
      </Section>

      <Section
        title="Seasons behind the funnel"
        aside={<span className="label">{data.seasons.filter((s) => s.status === 'ready').length} ready</span>}
      >
        <p className="max-w-2xl text-ink-2">
          The place through the seasons, each way, at the time frame the funnel spans: two months while a job is young, then six months, a year,
          two and three years as it runs on. Each strip covers its months of the calendar; the funnel lays the strips of the span that suits it along
          their dates, the full height under it, and paints the next span when the job grows into it.
        </p>
        {admin && !data.blocked ? (
          <div className="mt-4">
            <Button tone="secondary" busy={busy} onClick={() => void run(`/projects/${projectId}/art/seasons`)}>
              Paint what the funnel needs now
            </Button>
          </div>
        ) : null}
        {SEASON_SPANS.filter((span) => data.seasons.some((r) => r.span === span)).map((span) => (
          <div key={span} className="mt-8">
            <h3 className="font-display text-[1.25rem]">{SEASON_SPAN_WORDS[span]}</h3>
            {SEASON_KINDS.map((kind) => {
              const rows = data.seasons.filter((r) => r.kind === kind && r.span === span).sort((x, y) => x.month - y.month || x.variant - y.variant);
              if (!rows.length) return null;
              return (
                <div key={kind} className="mt-3">
                  <p className="flex flex-wrap items-baseline gap-x-3">
                    <span className="label">{SEASON_WAYS[kind].title}</span>
                    <span className="meta">{SEASON_WAYS[kind].note}</span>
                  </p>
                  <ul className={`mt-2 grid gap-4 ${kind === 'seasons_across' ? 'sm:grid-cols-2' : 'grid-cols-3 sm:max-w-xl'}`}>
                    {rows.map((row) => (
                      <li key={row.id} className="min-w-0">
                        <div className={`overflow-hidden rounded-[2px] bg-inset ${kind === 'seasons_across' ? 'aspect-[3/1]' : 'aspect-[1/2]'}`}>
                          {row.art ? <img src={`${row.art.url}?w=1024`} alt={row.art.alt} loading="lazy" decoding="async" className="h-full w-full object-contain" /> : null}
                        </div>
                        <p className="meta mt-1.5">
                          {stripWhen(row)} · {statusWord(row)}
                        </p>
                        {row.error ? <p className="text-sm text-alert">{row.error}</p> : null}
                        {admin && !data.blocked ? (
                          <Button
                            tone="quiet"
                            disabled={busy || row.status === 'pending'}
                            onClick={() => void run(`/projects/${projectId}/art/seasons`, { only: { kind, span: row.span, month: row.month } })}
                          >
                            Paint again
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ))}
      </Section>
    </Page>
  );
}

function Crop({ art, aspect, w = 640 }: { art: ArtDto; aspect: string; w?: number }) {
  return (
    <div className={`overflow-hidden rounded-[2px] bg-inset ${aspect}`}>
      <img src={`${art.url}?w=${w}`} alt={art.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ objectPosition: 'center 30%' }} />
    </div>
  );
}

/** The months a strip covers, in words: "March–April", "January–June", "A year from January". */
function stripWhen(row: SeasonArtRowDto): string {
  if (row.span >= 12) return row.variant > 1 ? `${SEASON_SPAN_WORDS[row.span]} · ${row.variant}` : SEASON_SPAN_WORDS[row.span];
  const months = seasonSlotMonths(row);
  return `${MONTHS[months[0]! - 1]}–${MONTHS[months.at(-1)! - 1]}`;
}

function statusWord(m: { status: ArtRowDto['status'] } | undefined): string {
  if (!m) return 'Not painted';
  return { pending: 'Painting…', ready: 'Ready', failed: 'Did not paint', retired: 'Replaced' }[m.status];
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
